import React, { useState, useRef, useEffect } from "react";
import { ChevronDownIcon } from "@heroicons/react/solid";

const ResponsiveTabs = ({ tabs, activeTab, onTabChange, variant = "border" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const activeTabData = tabs.find((t) => t.value === activeTab) || tabs[0];

  return (
    <>
      {/* Mobile dropdown */}
      <div className="sm:hidden relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center justify-between w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-900"
        >
          <span className="flex items-center gap-2">
            {activeTabData.icon && <activeTabData.icon className="w-4 h-4 text-primary-500" />}
            {activeTabData.label}
            {activeTabData.badge != null && (
              <span className="ml-1 px-1.5 py-0.5 text-xs rounded-full bg-primary-100 text-primary-600">
                {activeTabData.badge}
              </span>
            )}
          </span>
          <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
        </button>
        {isOpen && (
          <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
            {tabs.map((tab) => {
              const isActive = tab.value === activeTab;
              return (
                <button
                  key={tab.value}
                  onClick={() => {
                    onTabChange(tab.value);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm text-left ${
                    isActive
                      ? "bg-primary-50 text-primary-600 font-medium"
                      : "text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {tab.icon && <tab.icon className={`w-4 h-4 ${isActive ? "text-primary-500" : "text-gray-400"}`} />}
                  {tab.label}
                  {tab.badge != null && (
                    <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                      isActive ? "bg-primary-100 text-primary-600" : "bg-gray-100 text-gray-500"
                    }`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop tabs */}
      {variant === "underline" ? (
        <div className="hidden sm:block">
          <nav className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => onTabChange(tab.value)}
                className={`px-4 py-2 text-sm font-medium whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.value
                    ? "text-grey-900 underline underline-offset-8"
                    : "text-grey-500 hover:text-grey-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      ) : (
        <div className="hidden sm:block border-b border-gray-200">
          <nav className="flex space-x-4 -mb-px">
            {tabs.map((tab) => (
              <button
                key={tab.value}
                onClick={() => onTabChange(tab.value)}
                className={`py-3 px-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                  activeTab === tab.value
                    ? "border-primary-500 text-primary-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.icon && <tab.icon className={`w-4 h-4 ${activeTab === tab.value ? "text-primary-500" : "text-gray-400"}`} />}
                {tab.label}
                {tab.badge != null && (
                  <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${
                    activeTab === tab.value ? "bg-primary-100 text-primary-600" : "bg-gray-100 text-gray-500"
                  }`}>
                    {tab.badge}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>
      )}
    </>
  );
};

export default ResponsiveTabs;
